// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { getStoreObject } from './objectStoreService';
import type { SiteFinancialSummary } from '../types';

/**
 * Object-store key holding a site's daily savings roll-up. Only the object's
 * *attributes* are read — the CSV body stays in the store, so the fetch never
 * asks for a download URL.
 */
export const financialSummaryKey = (siteId: string): string =>
  `financial/${siteId}/daily_savings.csv`;

/**
 * Object-store attributes are string-only, and the listener writes `""` for a
 * metric it has no value for. Both empty and unparseable read as "no value"
 * rather than 0, so the UI can show an em dash instead of a fake zero.
 */
function num(attributes: Record<string, string>, key: string): number | undefined {
  const raw = attributes[key];
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function str(attributes: Record<string, string>, key: string): string | undefined {
  const raw = attributes[key];
  return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
}

/** Map the raw attribute bag onto the typed summary. */
export function parseFinancialSummary(
  attributes: Record<string, string> | undefined
): SiteFinancialSummary {
  const attrs = attributes ?? {};
  return {
    siteId: str(attrs, 'siteId'),
    asOfDate: str(attrs, 'asOfDate'),
    monthToDateExpectedSavingsUsd: num(attrs, 'monthToDateExpectedSavingsUsd'),
    monthToDateRealizedSavingsUsd: num(attrs, 'monthToDateRealizedSavingsUsd'),
    monthToDateMissedSavingsUsd: num(attrs, 'monthToDateMissedSavingsUsd'),
    monthToDateUnrealizedSavingsUsd: num(attrs, 'monthToDateUnrealizedSavingsUsd'),
    yearToDateExpectedSavingsUsd: num(attrs, 'yearToDateExpectedSavingsUsd'),
    yearToDateRealizedSavingsUsd: num(attrs, 'yearToDateRealizedSavingsUsd'),
    yearToDateMissedSavingsUsd: num(attrs, 'yearToDateMissedSavingsUsd'),
    monthToDateApprovedDays: num(attrs, 'monthToDateApprovedDays'),
    monthToDateUnapprovedDays: num(attrs, 'monthToDateUnapprovedDays'),
    monthToDateApprovalRatePct: num(attrs, 'monthToDateApprovalRatePct'),
    updatedAt: str(attrs, 'updatedAt'),
  };
}

/** Fetch and parse the financial KPI attributes for a site. */
export const getSiteFinancialSummary = async (siteId: string): Promise<SiteFinancialSummary> => {
  const { data } = await getStoreObject(siteId, financialSummaryKey(siteId));
  return parseFinancialSummary(data?.attributes);
};
